import styles from './CollectInfoForm.module.scss';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { CountryCode } from 'i18n/types';
import { Trans, useTranslation } from 'react-i18next';
import { useDecision } from '@optimizely/react-sdk';

import { TextField } from 'components/TextField';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { FieldError } from 'components/FieldError';

import { useCustomer } from 'hooks/context/customer/customer';
import { useMerchant } from 'hooks/context/merchant';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { useReCaptcha } from 'hooks/context/ReCaptcha';
import { useSmartNavigate } from 'hooks/useSmartNavigate';

import { parseAuthErrorMessage } from 'util/ErrorHandling/errorHandling.util';
import { isPhoneNumberValid, parsePhoneNumber } from 'util/Formatting';
import { generateId } from 'util/generateId';
import { validateEmail } from 'util/Regex';
import { RouteFC } from 'util/routeTypes';

import { COLLECT_INFO_FORM } from './const';
import { isCypressEnvironment } from 'util/Environment';
import { ImageModal } from 'components/ImageModal';
import { onClosePath } from '../Authentication.util';

type AuthenticationLocationStateProps = {
  redirectPath: string;
  basePath: string;
  defaultToEmail?: boolean;
  isDeliveryFlow?: boolean;
}

export const CollectInfoForm: RouteFC<{ children: never, props: RouteComponentProps }> = memo((props) => {
  const locationState = props.location?.state;
  const { redirectPath, basePath, defaultToEmail, isDeliveryFlow } = locationState as AuthenticationLocationStateProps || {};

  // Hooks
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { country } = useMerchant();
  const { sendPhoneCode, sendEmailAuth } = useCustomer();
  const { attemptReCaptcha } = useReCaptcha();
  const onClose = useCloseNavigate(onClosePath(redirectPath));
  const errorTextId = useRef(generateId()).current;
  const [decision] = useDecision('guest_checkout_a_b', { autoUpdate: true });

  // States
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [isPhone, setIsPhone] = useState(!defaultToEmail);
  const [isValid, setIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const showErrorState = useMemo(() => !!errorMessage, [errorMessage]);
  const showPhoneState = useMemo(() => isPhone && !showErrorState, [isPhone, showErrorState]);
  const showEmailState = useMemo(() => !isPhone && !showErrorState, [isPhone, showErrorState]);

  // Callbacks
  const validatePhoneNumber = useCallback((value?: string) => isPhoneNumberValid(value, country as CountryCode), [country]);
  const toggleContact = useCallback(() => setIsPhone((current: any) => !current), []);

  const emailFlowRedirectPath = useMemo(() => {
    if (isDeliveryFlow) {
      return basePath.includes('menu') ? '~address' : '~checkout/address';
    } else {
      return redirectPath;
    }
  }, [isDeliveryFlow, redirectPath, basePath]);

  const handleNavigate = useCallback(() => {
    const path = isPhone ? '.phone' : '.email';
    const state = isPhone ? { redirectPath, basePath, phoneNumber, isDeliveryFlow } : { redirectPath: emailFlowRedirectPath, emailAddress, isDeliveryFlow };

    navigate(path, {
      replace: true,
      state
    });

  }, [emailAddress, emailFlowRedirectPath, isDeliveryFlow, isPhone, navigate, phoneNumber, redirectPath, basePath]);

  const sendCode = useCallback(async () => {
    const reCaptchaToken = await attemptReCaptcha();

    try {
      if (isPhone) await sendPhoneCode(parsePhoneNumber(phoneNumber, country as CountryCode).number, reCaptchaToken);
      if (!isPhone) await sendEmailAuth(emailAddress, emailFlowRedirectPath, reCaptchaToken);

      handleNavigate();
    } catch (error) {
      setErrorMessage(parseAuthErrorMessage(error));
      setIsValid(false);
    }

  }, [attemptReCaptcha, isPhone, sendPhoneCode, phoneNumber, country, sendEmailAuth, emailAddress, emailFlowRedirectPath, handleNavigate]);

  const clearState = useCallback(() => {
    toggleContact();
    setErrorMessage('');
    setPhoneNumber('');
    setEmailAddress('');
  }, [toggleContact]);

  // Sets Validity whenever input value changes
  useEffect(() => {
    if (isPhone) setIsValid(validatePhoneNumber(phoneNumber));
    if (!isPhone) setIsValid(validateEmail(emailAddress));
  }, [emailAddress, isPhone, phoneNumber, validatePhoneNumber]);

  return (
    <ImageModal
      className={styles.modal}
      isOpen={true}
      title={t('authForm.title')}
      iconName={'login'}
      subtitle={t('authForm.subtitle')}
      onClose={onClose}
      testId={COLLECT_INFO_FORM.root}
      from={'onSignInClick'}
    >

      <section className={styles.body}>
        <div className={styles.inputFieldWrapper}>
          {showPhoneState && <TextField
            className={styles.inputField}
            label={t('authForm.phoneNumber')}
            labelClass={styles.label}
            name="phoneNumber"
            required={true}
            type="tel"
            validateField={(value) => validatePhoneNumber(value) || t<string>('contactForm.invalidPhone')}
            onChange={e => setPhoneNumber(e.target.value)}
            value={phoneNumber}
            data-testid={COLLECT_INFO_FORM.phone}
          />}
          {showEmailState && <TextField
            className={styles.inputField}
            labelClass={styles.label}
            label={t('authForm.emailAddress')}
            onChange={e => setEmailAddress(e.target.value)}
            required
            name="email"
            validateField={value => validateEmail(value) || t<string>('contactForm.invalidEmail')}
            value={emailAddress}
            data-testid={COLLECT_INFO_FORM.email}
          />}
          {showErrorState && (
            <FieldError id={errorTextId} data-testid={errorMessage} className={styles.errorText}>{t(`authErrorMessage.${errorMessage}`, { contact: phoneNumber ? parsePhoneNumber(phoneNumber, country as CountryCode).format('NATIONAL') : emailAddress })}</FieldError>
          )}
        </div>
        {!showErrorState && <div className={styles.subtitle}>
          <Trans i18nKey="authForm.switchContact">
            Sign up or log in with your <button className={styles.contact} onClick={toggleContact} data-testid={COLLECT_INFO_FORM.switch}>{{ contact: isPhone ? t('contactForm.email') : t('contactForm.phoneNumber') }}</button>instead
          </Trans>
        </div>}
      </section>
      <footer className={styles.footer}>
        {!showErrorState && <Button className={styles.button} onClick={sendCode} disabled={!isValid} data-testid={COLLECT_INFO_FORM.continue}>{t('authForm.continue')} </Button>}
        {showErrorState && <Button className={styles.button} onClick={clearState} data-testid={COLLECT_INFO_FORM.clearState}>{t(!isPhone ? 'authForm.loginAsPhone' : 'authForm.loginWithEmail')}</Button>}
        {(!decision.enabled || isCypressEnvironment()) && <Button variant={ButtonVariant.INVERTED} className={styles.button} onClick={() => navigate(redirectPath || '~menu')} data-testid={COLLECT_INFO_FORM.checkoutGuest}>{t('authForm.checkoutAsGuest')}</Button>}
      </footer>
    </ImageModal>
  );
});
